import React from 'react';
import { Link } from 'react-router-dom';
import {
  LuArrowLeft,
  LuArrowRight,
  LuBriefcase,
  LuBuilding2,
  LuCheck,
  LuCircleHelp,
  LuCreditCard,
  LuFileText,
  LuLandmark,
  LuLightbulb,
  LuPiggyBank,
  LuScrollText,
  LuShieldCheck,
  LuTrendingUp,
} from 'react-icons/lu';
import './PublicSite.css';
import Seo, { buildBreadcrumbSchema, buildFaqSchema } from '../components/Seo';
import {
  ASSET_HUB_PATH,
  assetCategories,
  assetHubMeta,
  getCategoryByPath,
  hubGridCategories,
  slugToCategory,
} from '../data/assetCategories';

const startAccount = () => {
  window.dispatchEvent(new Event('open-auth-modal'));
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const categoryIcons = {
  'bank-fd': LuLandmark,
  ppf: LuPiggyBank,
  epf: LuBriefcase,
  'ppf-epf': LuBriefcase,
  insurance: LuFileText,
  'stocks-mf': LuTrendingUp,
  property: LuBuilding2,
  loans: LuCreditCard,
  'property-loans': LuBuilding2,
  will: LuScrollText,
};

function SubPageHero({ eyebrow, title, lead, icon: Icon, variant = 'default', children }) {
  return (
    <section className={`sub-hero sub-hero-${variant}`}>
      {Icon && (
        <div className="sub-hero-icon" aria-hidden="true">
          <Icon />
        </div>
      )}
      <div className="sub-hero-copy">
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p className="lead">{lead}</p>
        {children}
      </div>
    </section>
  );
}

function SubPageCTA({ title, desc, buttonLabel = 'Secure Assets' }) {
  return (
    <>
      <section className="sub-cta">
        <div>
          <p className="eyebrow">GET STARTED</p>
          <h2>{title}</h2>
          <p>{desc}</p>
        </div>
        <button type="button" className="button button-accent" onClick={startAccount}>
          {buttonLabel} <LuArrowRight aria-hidden="true" />
        </button>
      </section>
      <div className="mobile-sticky-cta">
        <button type="button" className="button button-accent" onClick={startAccount}>
          {buttonLabel}
        </button>
      </div>
    </>
  );
}

function AssetBackLink() {
  return (
    <nav className="asset-back-nav" aria-label="Asset pages">
      <Link to={ASSET_HUB_PATH} className="asset-back-link">
        <LuArrowLeft aria-hidden="true" /> All assets
      </Link>
    </nav>
  );
}

function RelatedAssets({ slugs, currentSlug }) {
  const related = (slugs || [])
    .map((slug) => slugToCategory[slug])
    .filter(Boolean)
    .filter((c) => c.slug !== currentSlug);

  if (!related.length) return null;

  return (
    <section className="sub-section sub-section-soft">
      <div className="section-intro center">
        <p className="eyebrow">RELATED ASSETS</p>
        <h2>You may also want to record these</h2>
      </div>
      <div className="asset-related-grid">
        {related.map((item) => {
          const Icon = categoryIcons[item.slug] || LuShieldCheck;
          return (
            <Link to={item.path} className="asset-related-card" key={item.slug}>
              <Icon aria-hidden="true" />
              <span>{item.label}</span>
              <LuArrowRight aria-hidden="true" />
            </Link>
          );
        })}
      </div>
    </section>
  );
}

export function AssetCategoryPage({ path }) {
  const category = getCategoryByPath(path);
  if (!category) return null;

  const Icon = categoryIcons[category.slug] || LuShieldCheck;

  return (
    <>
      <Seo
        title={category.seo.title}
        description={category.seo.description}
        path={category.path}
        keywords={category.seo.keywords}
        jsonLd={[
          buildBreadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Assets', path: ASSET_HUB_PATH },
            { name: category.label, path: category.path },
          ]),
          ...(category.faqs?.length ? [buildFaqSchema(category.faqs)] : []),
        ]}
      />
      <div className="public-site public-page asset-category-page">
        <AssetBackLink />

        <SubPageHero
          eyebrow={category.hero.eyebrow}
          title={category.hero.title}
          lead={category.hero.lead}
          icon={Icon}
          variant={category.variant || 'default'}
        />

        <section className="sub-section">
          <div className="asset-intro-block">
            {category.intro.map((paragraph) => (
              <p key={paragraph.slice(0, 40)}>{paragraph}</p>
            ))}
          </div>
        </section>

        <section className="sub-section sub-section-soft">
          <div className="section-intro">
            <p className="eyebrow">WHY THIS MATTERS</p>
            <h2>{category.whyMatters.title}</h2>
          </div>
          <ul className="asset-check-list">
            {category.whyMatters.points.map((point) => (
              <li key={point}>
                <LuCheck aria-hidden="true" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="sub-section">
          <div className="section-intro">
            <p className="eyebrow">FOR YOUR FAMILY</p>
            <h2>What to record and what often goes wrong</h2>
          </div>
          <div className="sub-card-grid asset-detail-grid">
            <article className="sub-card sub-card-positive asset-detail-card">
              <LuFileText className="sub-card-icon" aria-hidden="true" />
              <h3>{category.whatToStore.title}</h3>
              <ul>
                {category.whatToStore.items.map((item) => (
                  <li key={item}>
                    <LuCheck aria-hidden="true" /> {item}
                  </li>
                ))}
              </ul>
            </article>
            <article className="sub-card sub-card-muted asset-detail-card">
              <LuCircleHelp className="sub-card-icon" aria-hidden="true" />
              <h3>{category.commonProblems.title}</h3>
              <ul>
                {category.commonProblems.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          </div>
        </section>

        <section className="sub-section sub-section-soft">
          <div className="section-intro">
            <p className="eyebrow">PRACTICAL TIPS</p>
            <h2>{category.tips.title}</h2>
          </div>
          <div className="asset-tips-grid">
            {category.tips.items.map((tip, index) => (
              <article className="asset-tip-card" key={tip}>
                <div className="asset-tip-head">
                  <span className="asset-tip-icon-wrap">
                    <LuLightbulb aria-hidden="true" />
                  </span>
                  <span className="asset-tip-num">{index + 1}</span>
                </div>
                <p>{tip}</p>
              </article>
            ))}
          </div>
        </section>

        {category.faqs?.length > 0 && (
          <section className="sub-section">
            <div className="section-intro center">
              <p className="eyebrow">COMMON QUESTIONS</p>
              <h2>Quick answers about {category.label}</h2>
            </div>
            <div className="faq-list faq-list-page">
              {category.faqs.map(({ q, a }) => (
                <details className="faq-item" key={q}>
                  <summary>{q}</summary>
                  <p>{a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        <RelatedAssets slugs={category.related} currentSlug={category.slug} />

        <SubPageCTA
          title={`Start recording your ${category.label} today`}
          desc="Free to begin. Add your first asset and nominee in minutes—your family will thank you later."
          buttonLabel="Secure Assets"
        />
      </div>
    </>
  );
}

export function AssetsHubPage() {
  return (
    <>
      <Seo
        title={assetHubMeta.seo.title}
        description={assetHubMeta.seo.description}
        path={assetHubMeta.path}
        keywords={assetHubMeta.seo.keywords}
        jsonLd={buildBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Assets', path: ASSET_HUB_PATH },
        ])}
      />
      <div className="public-site public-page asset-hub-page">
        <SubPageHero
          eyebrow={assetHubMeta.hero.eyebrow}
          title={assetHubMeta.hero.title}
          lead={assetHubMeta.hero.lead}
          icon={LuShieldCheck}
          variant="mint"
        />

        <section className="sub-section">
          <div className="section-intro center">
            <p className="eyebrow">CHOOSE A CATEGORY</p>
            <h2>Tap any asset type to learn what to record and why it matters</h2>
            <p>Each page explains what families often miss—and how KeepMyAsset helps.</p>
          </div>
          <div className="asset-hub-grid">
            {hubGridCategories.map((category) => {
              const Icon = categoryIcons[category.slug] || LuShieldCheck;
              return (
                <Link to={category.path} className="asset-hub-card" key={category.slug}>
                  <div className="asset-hub-card-icon">
                    <Icon aria-hidden="true" />
                  </div>
                  <h3>{category.gridLabel || category.label}</h3>
                  <p>{category.hero.lead.slice(0, 120)}…</p>
                  <span className="asset-hub-card-link">
                    Learn more <LuArrowRight aria-hidden="true" />
                  </span>
                </Link>
              );
            })}
          </div>
        </section>

        <section className="sub-section sub-section-soft">
          <div className="section-intro center">
            <p className="eyebrow">COMBINED GUIDES</p>
            <h2>Related assets often go together</h2>
          </div>
          <div className="asset-related-grid">
            {[slugToCategory['ppf-epf'], slugToCategory['property-loans']].filter(Boolean).map((item) => {
              const Icon = categoryIcons[item.slug];
              return (
                <Link to={item.path} className="asset-related-card" key={item.slug}>
                  <Icon aria-hidden="true" />
                  <span>{item.label}</span>
                  <LuArrowRight aria-hidden="true" />
                </Link>
              );
            })}
          </div>
        </section>

        <SubPageCTA
          title="One place for every asset your family should know about"
          desc="Store bank accounts, PPF, EPF, insurance, investments, property, loans, and wills—securely."
          buttonLabel="Secure Assets"
        />
      </div>
    </>
  );
}

export function resolveAssetPage(path) {
  if (path === ASSET_HUB_PATH) return <AssetsHubPage />;
  if (getCategoryByPath(path)) return <AssetCategoryPage path={path} />;
  return null;
}

export { assetCategories, ASSET_HUB_PATH };
